import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Outlet } from "react-router-dom";
import { Toaster } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ReserveYourStayCTA from "@/components/ReserveYourStayCTA";
import Concierge from "@/components/Concierge";
import WhatsAppButton from "@/components/WhatsAppButton";
import Home from "@/pages/Home";
import Rooms from "@/pages/Rooms";
import RoomDetail from "@/pages/RoomDetail";
import Amenities from "@/pages/Amenities";
import Gallery from "@/pages/Gallery";
import Experiences from "@/pages/Experiences";
import LocalArea from "@/pages/LocalArea";
import Offers from "@/pages/Offers";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Booking from "@/pages/Booking";

// Admin (hidden — not linked from the public site, noindex meta injected at runtime)
import { AdminAuthProvider } from "@/lib/adminAuth";
import AdminProtectedRoute from "@/components/AdminProtectedRoute";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminForgotPassword from "@/pages/admin/AdminForgotPassword";
import AdminResetPassword from "@/pages/admin/AdminResetPassword";
import AdminChangePassword from "@/pages/admin/AdminChangePassword";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminStaff from "@/pages/admin/AdminStaff";
import AdminReservations from "@/pages/admin/AdminReservations";
import AdminReservationDetail from "@/pages/admin/AdminReservationDetail";
import AdminReservationNew from "@/pages/admin/AdminReservationNew";
import AdminMedia from "@/pages/admin/AdminMedia";
import AdminRooms from "@/pages/admin/AdminRooms";
import AdminCalendar from "@/pages/admin/AdminCalendar";
import AdminGuests from "@/pages/admin/AdminGuests";
import AdminReports from "@/pages/admin/AdminReports";
import AdminAuditLog from "@/pages/admin/AdminAuditLog";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminLoyalty from "@/pages/admin/AdminLoyalty";
import AdminVerification from "@/pages/admin/AdminVerification";
import AdminPhasePlaceholder from "@/pages/admin/AdminPhasePlaceholder";

// Customer accounts (public but auth-gated at /account)
import { CustomerAuthProvider } from "@/lib/customerAuth";
import SignIn from "@/pages/SignIn";
import SignUp from "@/pages/SignUp";
import AuthCallback from "@/pages/AuthCallback";
import AccountLayout from "@/pages/account/AccountLayout";
import AccountOverview from "@/pages/account/AccountOverview";
import { AccountBookings, AccountBookingDetail } from "@/pages/account/AccountBookings";
import AccountLoyalty from "@/pages/account/AccountLoyalty";
import AccountProfile from "@/pages/account/AccountProfile";
import AccountVerification from "@/pages/account/AccountVerification";
import { ForgotPassword, ResetPassword, VerifyEmail } from "@/pages/account/AccountAuthPages";

function ScrollToTop() {
    const { pathname } = useLocation();
    useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
    return null;
}

// Injects robots noindex,nofollow whenever the user is on an /admin/* route,
// so search engines never see the dashboard even if a URL leaks.
function AdminNoIndex() {
    const { pathname } = useLocation();
    useEffect(() => {
        const isAdmin = pathname.startsWith("/admin");
        let meta = document.querySelector('meta[name="robots"]');
        if (isAdmin) {
            if (!meta) {
                meta = document.createElement("meta");
                meta.setAttribute("name", "robots");
                document.head.appendChild(meta);
            }
            meta.setAttribute("content", "noindex, nofollow, noarchive, nosnippet");
            document.title = "Serenity Cove · Admin";
        } else if (meta && meta.getAttribute("data-admin") === "1") {
            meta.remove();
        }
    }, [pathname]);
    return null;
}

function PublicShell() {
    const { pathname } = useLocation();
    const showReserveCTA = pathname !== "/booking";
    return (
        <div className="min-h-screen bg-ivory text-navy">
            <Navbar />
            <main>
                <Outlet />
            </main>
            {showReserveCTA && <ReserveYourStayCTA />}
            <Concierge />
            <WhatsAppButton />
            <Footer />
        </div>
    );
}

export default function App() {
    return (
        <BrowserRouter>
            <CustomerAuthProvider>
                <AdminAuthProvider>
                    <ScrollToTop />
                    <AdminNoIndex />
                    <RootRouter />
                    <Toaster position="top-center" richColors />
                </AdminAuthProvider>
            </CustomerAuthProvider>
        </BrowserRouter>
    );
}

// Synchronously detect OAuth `session_id=` in the URL hash BEFORE React Router
// dispatches, so the AuthCallback screen handles it — otherwise a global auth
// check can race and 401 before the cookie is set.
function RootRouter() {
    const location = useLocation();
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    return (
        <Routes>
            {/* Public site */}
            <Route element={<PublicShell />}>
                <Route path="/" element={<Home />} />
                <Route path="/rooms" element={<Rooms />} />
                <Route path="/rooms/:slug" element={<RoomDetail />} />
                <Route path="/amenities" element={<Amenities />} />
                <Route path="/gallery" element={<Gallery />} />
                <Route path="/experiences" element={<Experiences />} />
                <Route path="/local-area" element={<LocalArea />} />
                <Route path="/offers" element={<Offers />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/booking" element={<Booking />} />
                {/* Customer auth (public) */}
                <Route path="/signin" element={<SignIn />} />
                <Route path="/signup" element={<SignUp />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password/:token" element={<ResetPassword />} />
                <Route path="/verify-email/:token" element={<VerifyEmail />} />
            </Route>

            {/* Customer account — auth gated */}
            <Route path="/account" element={<AccountLayout />}>
                <Route index element={<AccountOverview />} />
                <Route path="bookings" element={<AccountBookings />} />
                <Route path="bookings/:id" element={<AccountBookingDetail />} />
                <Route path="verification/:reservationId" element={<AccountVerification />} />
                <Route path="loyalty" element={<AccountLoyalty />} />
                <Route path="profile" element={<AccountProfile />} />
            </Route>

            {/* Hidden admin — public auth pages */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
            <Route path="/admin/reset-password" element={<AdminResetPassword />} />

            {/* Authenticated change-password (used for first-login flow too) */}
            <Route path="/admin/change-password" element={
                <AdminProtectedRoute><AdminChangePassword /></AdminProtectedRoute>
            } />

            {/* Authenticated admin app */}
            <Route path="/admin" element={
                <AdminProtectedRoute><AdminLayout /></AdminProtectedRoute>
            }>
                <Route index element={<AdminDashboard />} />
                <Route path="staff" element={
                    <AdminProtectedRoute roles={["super_admin"]}><AdminStaff /></AdminProtectedRoute>
                } />
                <Route path="reservations" element={
                    <AdminProtectedRoute roles={["reservations_manager"]}>
                        <AdminReservations />
                    </AdminProtectedRoute>
                } />
                <Route path="reservations/new" element={
                    <AdminProtectedRoute roles={["reservations_manager"]}>
                        <AdminReservationNew />
                    </AdminProtectedRoute>
                } />
                <Route path="reservations/:id" element={
                    <AdminProtectedRoute roles={["reservations_manager"]}>
                        <AdminReservationDetail />
                    </AdminProtectedRoute>
                } />
                <Route path="calendar" element={
                    <AdminProtectedRoute roles={["reservations_manager"]}>
                        <AdminCalendar />
                    </AdminProtectedRoute>
                } />
                <Route path="rooms" element={
                    <AdminProtectedRoute roles={["content_manager"]}>
                        <AdminRooms />
                    </AdminProtectedRoute>
                } />
                <Route path="media" element={
                    <AdminProtectedRoute roles={["content_manager"]}>
                        <AdminMedia />
                    </AdminProtectedRoute>
                } />
                <Route path="guests" element={
                    <AdminProtectedRoute roles={["reservations_manager"]}>
                        <AdminGuests />
                    </AdminProtectedRoute>
                } />
                <Route path="loyalty" element={
                    <AdminProtectedRoute roles={["reservations_manager"]}>
                        <AdminLoyalty />
                    </AdminProtectedRoute>
                } />
                <Route path="verification" element={
                    <AdminProtectedRoute roles={["reservations_manager"]}>
                        <AdminVerification />
                    </AdminProtectedRoute>
                } />
                <Route path="reports" element={
                    <AdminProtectedRoute roles={["reservations_manager"]}>
                        <AdminReports />
                    </AdminProtectedRoute>
                } />
                <Route path="audit-log" element={
                    <AdminProtectedRoute roles={["super_admin"]}>
                        <AdminAuditLog />
                    </AdminProtectedRoute>
                } />
                <Route path="settings" element={
                    <AdminProtectedRoute roles={["super_admin"]}>
                        <AdminSettings />
                    </AdminProtectedRoute>
                } />
            </Route>
        </Routes>
    );
}
